import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSiteSettings } from "../../contexts/SiteSettingsContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, FlaskConical, Save } from "lucide-react";
import { sendFunnelEvent } from "../../lib/funnelEvents";
import { track as trackMetaPixelEvent } from "../../lib/metaPixel";
import { trackContactClick } from "../../lib/ga4";

const ADS_FIELDS = [
    ["google_ads_conversion_id", "Google Ads Conversion ID", "AW-18347689988"],
    ["google_ads_qualified_lead_label", "Qualified Lead Label", "AbCdEFgh12"],
    ["google_ads_complete_registration_label", "Complete Registration Label", "XyZ123abc"],
    ["google_ads_begin_checkout_label", "Begin Checkout Label", "Begin1234"],
    ["google_ads_purchase_label", "Purchase Label", "Buy98765"],
    ["google_ads_application_approved_label", "Application Approved Label", "Approved778"],
    ["google_ads_whatsapp_label", "WhatsApp Click Label", "WaClick001"],
    ["google_ads_telegram_label", "Telegram Click Label", "TgClick001"],
];

const ANALYTICS_FIELDS = [
    ["meta_pixel_id", "Meta Pixel ID", "123456789012345"],
    ["ga4_measurement_id", "GA4 Measurement ID", "G-XXXXXXXXXX"],
    ["ga4_api_secret", "GA4 API Secret", "a1b2c3d4e5f6"],
    ["google_site_verification", "Google Site Verification", "google-site-verification=xxxx"],
];

const EVENT_ROWS = [
    ["Kayıt tamamlandı", "complete_registration", "CompleteRegistration", "complete_registration"],
    ["Nitelikli lead", "qualified_lead", "QualifiedLead", "qualified_lead"],
    ["Checkout başlat", "begin_checkout", "InitiateCheckout", "begin_checkout"],
    ["Satın alma", "purchase", "Purchase", "purchase"],
    ["WhatsApp tıklama", "contact_whatsapp", "Contact", "contact"],
    ["Telegram tıklama", "contact_telegram", "Contact", "contact"],
];

export default function AdminAdsTrackingCenter() {
    const { settings, refresh } = useSiteSettings();
    const [draft, setDraft] = useState(settings);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(settings);
    }, [settings]);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/site-settings", { settings: draft });
            toast.success("Ads Tracking Center ayarları kaydedildi");
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Kaydetme başarısız");
        } finally {
            setSaving(false);
        }
    };

    const triggerTest = async (eventName) => {
        try {
            await sendFunnelEvent(eventName, {
                content_name: `ads_center_test_${eventName}`,
                content_category: "ads_tracking_center",
                value: eventName === "purchase" ? 99 : 1,
                currency: "USD",
                page_path: "/admin/ads-tracking",
                page_title: "Ads Tracking Center",
            });

            if (eventName === "contact_whatsapp") {
                trackMetaPixelEvent("Contact", { channel: "whatsapp", placement: "ads_center_test" });
                trackContactClick("whatsapp", { placement: "ads_center_test" });
            }
            if (eventName === "contact_telegram") {
                trackMetaPixelEvent("Contact", { channel: "telegram", placement: "ads_center_test" });
                trackContactClick("telegram", { placement: "ads_center_test" });
            }

            toast.success(`${eventName} test olayı gönderildi`);
        } catch {
            toast.error("Test olayı gönderilemedi");
        }
    };

    return (
        <div data-testid="admin-ads-tracking-center" className="space-y-6">
            <div>
                <h1 className="font-display font-bold text-3xl tracking-tight">Ads Tracking Center</h1>
                <p className="text-muted-foreground mt-1">Google/Meta tracking ayarları, format rehberi ve test olayları.</p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="ads-format-guide">
                <h2 className="font-semibold mb-3">Ne nereye yazılacak? (çok net)</h2>
                <ol className="text-sm space-y-2 text-muted-foreground list-decimal pl-5">
                    <li>
                        <strong>Google Ads Conversion ID</strong> kutusuna sadece <code>AW-...</code> yazın. Örn: <code>AW-18347689988</code>
                    </li>
                    <li>
                        <strong>Label</strong> kutularına sadece <code>/</code> sonrası kısmı yazın.
                        Örn: <code>AW-18347689988/AbCdEFgh12</code> ise label = <code>AbCdEFgh12</code>
                    </li>
                    <li>
                        Label’ı bulmak için: <strong>Google Ads → Goals → Conversions → action → Tag setup</strong>
                    </li>
                </ol>
            </div>

            <div className="grid xl:grid-cols-2 gap-5">
                <div className="rounded-2xl border border-white/10 bg-card p-5 space-y-3" data-testid="ads-google-fields-card">
                    <h3 className="font-semibold">Google Ads Alanları</h3>
                    {ADS_FIELDS.map(([key, label, ph]) => (
                        <div key={key} className="space-y-1">
                            <Label htmlFor={key}>{label}</Label>
                            <Input
                                id={key}
                                data-testid={`ads-field-${key}`}
                                value={draft?.[key] ?? ""}
                                placeholder={ph}
                                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                                className="bg-background border-white/10"
                            />
                        </div>
                    ))}
                </div>

                <div className="rounded-2xl border border-white/10 bg-card p-5 space-y-3" data-testid="ads-analytics-fields-card">
                    <h3 className="font-semibold">Meta + GA4 Alanları</h3>
                    {ANALYTICS_FIELDS.map(([key, label, ph]) => (
                        <div key={key} className="space-y-1">
                            <Label htmlFor={key}>{label}</Label>
                            <Input
                                id={key}
                                data-testid={`ads-field-${key}`}
                                value={draft?.[key] ?? ""}
                                placeholder={ph}
                                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                                className="bg-background border-white/10"
                            />
                        </div>
                    ))}

                    <Button data-testid="ads-settings-save" onClick={save} disabled={saving} className="w-full bg-primary text-primary-foreground">
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
                    </Button>
                </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="ads-event-map-card">
                <h3 className="font-semibold mb-3">Hangi buton hangi event’i gönderiyor?</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="ads-event-map-table">
                        <thead className="text-xs uppercase text-muted-foreground border-b border-white/10">
                            <tr>
                                <th className="text-left py-2 pr-3">Aksiyon</th>
                                <th className="text-left py-2 pr-3">Backend Event</th>
                                <th className="text-left py-2 pr-3">Meta Event</th>
                                <th className="text-left py-2">GA4 Event</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {EVENT_ROWS.map((r) => (
                                <tr key={r[0]}>
                                    <td className="py-2 pr-3">{r[0]}</td>
                                    <td className="py-2 pr-3 font-mono text-xs">{r[1]}</td>
                                    <td className="py-2 pr-3 font-mono text-xs">{r[2]}</td>
                                    <td className="py-2 font-mono text-xs">{r[3]}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-card p-5" data-testid="ads-event-tests-card">
                <h3 className="font-semibold mb-3">Test olayları</h3>
                <div className="flex flex-wrap gap-2">
                    {EVENT_ROWS.map((r) => (
                        <Button
                            key={r[1]}
                            variant="outline"
                            onClick={() => triggerTest(r[1])}
                            data-testid={`ads-test-${r[1]}`}
                            className="border-white/15"
                        >
                            <FlaskConical className="w-4 h-4 mr-2" /> {r[1]}
                        </Button>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 inline-flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Teste bastığınızda hem backend funnel hem ilgili client tracking tetiklenir.
                </p>
            </div>
        </div>
    );
}
